// frontend/src/pages/PayrollDone/helpers.ts
// Pure helpers for the Payroll Done page: auth, formatting, CSV download.

import type { ScheduleFrequency, DoneViewRun } from "./types";

/**
 * Hardcoded API base URL — matches original behavior.
 * Migration to process.env.REACT_APP_API_URL can happen in a later cleanup PR.
 */
export const API_URL = "https://api.getnovala.com";

/**
 * Read the auth token from localStorage. Checks current + legacy keys.
 */
export function getToken(): string {
  return (
    localStorage.getItem("access_token") ||
    localStorage.getItem("token") ||
    ""
  );
}

/**
 * Bearer-token + JSON content-type headers for API requests with a body.
 */
export function authHeaders(): Record<string, string> {
  return {
    Authorization: "Bearer " + getToken(),
    "Content-Type": "application/json",
  };
}

/**
 * Format a number as en-CA dollars with 2 decimals.
 */
export function fmtMoney(n: number | string | null | undefined): string {
  const v = Number(n) || 0;
  return "$" + v.toLocaleString("en-CA", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

/**
 * Format a YYYY-MM-DD date string as dd/mm/yyyy.
 * Does string splitting (not Date parsing) to avoid timezone drift on date-only strings.
 */
export function fmtDate(iso?: string | null): string {
  if (!iso) return "";
  const parts = String(iso).split("-");
  if (parts.length < 3) return "";
  return parts[2] + "/" + parts[1] + "/" + parts[0];
}

/**
 * Format a YYYY-MM-DD date string as "Day, dd/mm/yyyy" (e.g. "Fri, 05/10/2026").
 */
export function fmtDateFull(iso?: string | null): string {
  if (!iso) return "";
  const dt = new Date(iso + "T00:00:00");
  const days = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
  return days[dt.getDay()] + ", " + fmtDate(iso);
}

/**
 * Human-readable label for a schedule frequency string.
 */
export function scheduleFrequencyLabel(freq?: ScheduleFrequency | null): string {
  if (!freq) return "";
  const map: Record<string, string> = {
    weekly: "Weekly",
    biweekly: "Biweekly",
    semimonthly: "Semi-monthly",
    monthly: "Monthly",
  };
  return map[freq] || freq;
}

/**
 * Download the pay run as a CSV. Filename includes the pay period dates (dd-mm-yyyy_to_dd-mm-yyyy).
 * Server builds the CSV; this just streams the blob to a download.
 */
export async function downloadPayRunCsv(
  payRunId: string,
  run: DoneViewRun
): Promise<void> {
  const token = getToken();
  const res = await fetch(
    API_URL + "/api/v1/payroll/runs/" + payRunId + "/export",
    { headers: { Authorization: "Bearer " + token } }
  );
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  const startStr = fmtDate(run.pay_period_start).replace(/\//g, "-");
  const endStr = fmtDate(run.pay_period_end).replace(/\//g, "-");
  a.download = "payroll_" + startStr + "_to_" + endStr + ".csv";
  a.click();
  URL.revokeObjectURL(url);
}